import { useState, useEffect } from 'react';
import { SiteController, CreateSiteControllerData, UpdateSiteControllerData } from '../../lib/site-controllers';
import { Save } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';

interface SiteControllerFormModalProps {
  controller: SiteController | null;
  siteId: string;
  onSubmit: (data: CreateSiteControllerData | UpdateSiteControllerData) => void;
  onClose: () => void;
  isSubmitting: boolean;
}

export default function SiteControllerFormModal({
  controller,
  siteId,
  onSubmit,
  onClose,
  isSubmitting,
}: SiteControllerFormModalProps) {
  const isEditing = !!controller;
  const [formData, setFormData] = useState({
    name: '',
    ipAddress: '',
  });

  useEffect(() => {
    if (controller) {
      setFormData({
        name: controller.name,
        ipAddress: controller.ipAddress || '',
      });
    } else {
      setFormData({
        name: '',
        ipAddress: '',
      });
    }
  }, [controller]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData: CreateSiteControllerData | UpdateSiteControllerData = {
      name: formData.name,
      ipAddress: formData.ipAddress || undefined,
    };

    // For create, include siteId
    if (!isEditing) {
      (submitData as CreateSiteControllerData).siteId = siteId;
    }

    onSubmit(submitData);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Site Controller' : 'Add Site Controller'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the controller name and optional IP address.'
              : 'Create a new controller for this site by providing a name and optional IP address.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              Name *
            </Label>
            <Input
              id="name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Main Controller"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ipAddress">
              IP Address
            </Label>
            <Input
              id="ipAddress"
              type="text"
              value={formData.ipAddress}
              onChange={(e) => setFormData({ ...formData, ipAddress: e.target.value })}
              placeholder="e.g., 192.168.1.100"
            />
          </div>

          {isEditing && controller && (
            <Card className="p-3">
              <p className="text-xs text-muted-foreground mb-1">API Key (read-only)</p>
              <p className="text-xs font-mono break-all">{controller.apiKey}</p>
              <p className="text-xs text-muted-foreground mt-2">
                Status:{' '}
                <span
                  className={
                    controller.state === 'online'
                      ? 'text-green-600'
                      : controller.state === 'warning'
                        ? 'text-amber-600'
                        : 'text-muted-foreground'
                  }
                >
                  {controller.state === 'online'
                    ? 'Online'
                    : controller.state === 'warning'
                      ? 'Warning'
                      : 'Offline'}
                </span>
              </p>
              {controller.lastSeen && (
                <p className="text-xs text-muted-foreground mt-1">
                  Last seen: {new Date(controller.lastSeen).toLocaleString()}
                </p>
              )}
            </Card>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              <Save className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
